// frontend/src/pages/teacher/GroupMembersPage.js (فایل جدید)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../../modules/api';
import { FaUsers, FaSpinner, FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';

// کامپوننت آواتار
const Avatar = ({ name }) => {
    const initial = name ? name.charAt(0) : '?';
    return <div className="avatar">{initial}</div>;
};

// کامپوننت لیست دانش‌آموزان
const StudentList = ({ title, students, actionText, onAction, loading }) => (
    <div className="data-table-container mb-4">
        <h3 className="table-title" style={{marginTop: '0'}}>{title}</h3>
        <table className="data-table">
            <tbody>
                {loading && <tr><td colSpan="3" className="text-center"><FaSpinner className="spinner" /></td></tr>}
                {!loading && students.length === 0 && <tr><td colSpan="3" className="text-center p-4 text-gray-400">لیست خالی است.</td></tr>}
                {students.map(student => (
                    <tr key={student.id}>
                        <td>
                            <div className="user-cell">
                                <Avatar name={student.full_name || student.username} />
                                <span>{student.full_name || student.username}</span>
                            </div>
                        </td>
                        <td className="text-gray-400">{student.username}</td>
                        <td>
                            <button 
                                className={`button ${actionText === "حذف" ? "button-secondary" : "button-primary"}`}
                                style={{fontSize: '0.8rem', padding: '5px 10px'}}
                                onClick={() => onAction(student.id)}
                            >
                                {actionText === "حذف" ? <FaTrash /> : <FaPlus />}
                                {actionText}
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);


const GroupMembersPage = () => {
    const { groupId } = useParams();
    const [group, setGroup] = useState(null);
    const [allStudents, setAllStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // تابع اصلی واکشی داده‌ها
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // ۱. دریافت جزئیات گروه (شامل اعضای فعلی و ID دوره)
            const groupRes = await axiosInstance.get(`/core/teacher/groups/${groupId}/`);
            setGroup(groupRes.data);
            const courseId = groupRes.data.course; // ID دوره را از گروه می‌گیریم

            if (courseId) {
                // ۲. دریافت *تمام* دانش‌آموزان آن دوره
                const studentsRes = await axiosInstance.get(`/core/teacher/courses/${courseId}/students/`);
                setAllStudents(studentsRes.data);
            }
            
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(err.response?.data?.detail || "خطا در بارگذاری اطلاعات.");
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // تابع مدیریت افزودن/حذف عضو
    const manageMember = async (studentId, action) => {
        try {
            await axiosInstance.post(`/core/teacher/groups/${groupId}/manage-members/`, {
                student_id: studentId,
                action: action // 'add' or 'remove'
            });
            // پس از موفقیت، داده‌ها را دوباره واکشی کن تا لیست‌ها آپدیت شوند
            await fetchData(); 
        } catch (err) {
            alert(err.response?.data?.detail || `خطا در ${action === 'add' ? 'افزودن' : 'حذف'} عضو.`);
        }
    };

    // محاسبات ممو برای جدا کردن دو لیست
    const { members, availableStudents } = useMemo(() => {
        if (!group) return { members: [], availableStudents: [] };
        
        const memberIds = new Set(group.members.map(m => m.id));
        
        const members = allStudents.filter(s => memberIds.has(s.id));
        const availableStudents = allStudents.filter(s => !memberIds.has(s.id));
        
        return { members, availableStudents };
    }, [group, allStudents]);


    if (loading && !group) {
        return <div className="page-container text-center"><FaSpinner className="spinner text-3xl" /></div>;
    }

    if (error) {
        return <div className="page-container alert alert-danger">{error}</div>;
    }

    return (
        <div className="page-container" style={{maxWidth: '1000px'}}>
            <Link 
                to={`/teacher/courses/${group?.course}/groups`} 
                className="button button-secondary mb-6 inline-flex items-center"
            >
                <FaArrowLeft className="ml-2" /> بازگشت به مدیریت گروه‌ها
            </Link>

            <div className="page-header" style={{paddingBottom: '10px', marginBottom: '20px'}}>
                <div>
                    <h1 className="flex items-center gap-3"><FaUsers /> مدیریت اعضای گروه</h1>
                    <p>گروه: {group?.name} (دوره: {group?.course_title})</p>
                </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* ستون اول: اعضای فعلی گروه */}
                <StudentList
                    title="اعضای گروه"
                    students={members}
                    actionText="حذف"
                    onAction={(studentId) => manageMember(studentId, 'remove')}
                    loading={loading}
                />
                
                {/* ستون دوم: دانش‌آموزان قابل افزودن */}
                <StudentList
                    title="دانش‌آموزان دوره (جهت افزودن)"
                    students={availableStudents}
                    actionText="افزودن"
                    onAction={(studentId) => manageMember(studentId, 'add')}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default GroupMembersPage;